import { type ComponentPropsWithoutRef } from 'react';
import Modal from '@/components/Modal';
import type { RefetchOptions, QueryObserverResult } from '@tanstack/react-query';
import type { AgentApiCompaniesInformation, AgentApiCompaniesShow } from '@/types/serializers';
import { Button } from '@/components/Button';
import useUpdateCompanyProfile from '../api/useUpdateCompanyProfile';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';
import useFetchIndustries from '../../api/useFetchIndustries';
import { LISTED_PHASE_ITEMS } from '../constants';

type Props = {
  companyId: number;
  modal: Omit<ComponentPropsWithoutRef<typeof Modal>, 'children'>;
  suggestionInfo: AgentApiCompaniesInformation;
  close: () => void;
  companyRefetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<AgentApiCompaniesShow, Error>>;
};

export default function CompaniesImportModal({
  modal,
  close,
  suggestionInfo,
  companyId,
  companyRefetch,
}: Props) {
  const { mutateAsync: updateProfile } = useUpdateCompanyProfile();
  const { data: industries } = useFetchIndustries();

  const getIndustryIdByName = (name: string | null) => {
    if (!name || !industries) return undefined;
    const industry = industries.find((ind) => ind.name === name);
    return industry ? industry.id : undefined;
  };

  const getListedPhaseValue = (label: string) => {
    const item = LISTED_PHASE_ITEMS.find((item) => item.label === label);
    return item?.value || 'unknown';
  };

  const handleImport = async () => {
    try {
      const listedPhaseValue = LISTED_PHASE_ITEMS.some(
        (item) => item.value === suggestionInfo.listed_phase
      )
        ? suggestionInfo.listed_phase
        : getListedPhaseValue(suggestionInfo.listed_phase);

      const requestData = {
        companyId: companyId,
        company_information: {
          description: suggestionInfo.description || undefined,
          president_name: suggestionInfo.president_name || undefined,
          listed_phase: listedPhaseValue as
            | 'unlisted'
            | 'prime_market'
            | 'standard_market'
            | 'growth_market'
            | 'jasdaq'
            | 'tokyo_pro'
            | 'unknown',
          established_date: suggestionInfo.established_date || undefined,
          number_of_employees: suggestionInfo.number_of_employees,
          address: suggestionInfo.address || undefined,
          official_url: suggestionInfo.official_url || undefined,
          industry_id: getIndustryIdByName(suggestionInfo.industry),
          corporate_number: suggestionInfo.corporate_number ?? '',
        },
      };

      await updateProfile(requestData);
      await companyRefetch();
      showSuccessToast('企業情報をインポートしました');
      close();
    } catch (error) {
      showErrorToast('インポートに失敗しました');
    }
  };

  const fields = [
    { label: '事業内容', value: suggestionInfo.description, isLongText: true },
    { label: '代表者名', value: suggestionInfo.president_name },
    { label: '上場情報', value: suggestionInfo.listed_phase },
    { label: '設立年', value: suggestionInfo.established_date },
    { label: '従業員数', value: suggestionInfo.number_of_employees?.toString() },
    { label: '住所', value: suggestionInfo.address },
    { label: '公式URL', value: suggestionInfo.official_url },
    { label: '業種', value: suggestionInfo.industry },
  ];
  return (
    <Modal {...modal}>
      <div className="w-[1094px] rounded bg-white">
        <div className="flex h-[56px] items-center gap-4 border-b border-solid border-border px-6">
          <p className="text-base font-bold">この企業情報をインポートする</p>
        </div>
        <div className="max-h-[557px] overflow-hidden overflow-y-auto p-lg">
          <div className="flex flex-col gap-7">
            {(() => {
              let skipNext = false;
              return fields.map((field, index) => {
                if (skipNext) {
                  skipNext = false;
                  return null;
                }

                if (field.isLongText) {
                  return (
                    <div key={field.label} className="w-full">
                      <p className="text-sm font-bold">{field.label}</p>
                      <p className="mt-2 text-sm leading-[170%] whitespace-pre-wrap">
                        {field.value || '未設定'}
                      </p>
                    </div>
                  );
                } else {
                  const nextField = index + 1 < fields.length ? fields[index + 1] : null;
                  const isNextShort = nextField && !nextField.isLongText;

                  if (isNextShort) {
                    skipNext = true;
                  }

                  return (
                    <div
                      key={`row-${index}`}
                      className="grid w-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-[30px]"
                    >
                      <div>
                        <p className="text-sm font-bold">{field.label}</p>
                        <p className="mt-2 text-sm">{field.value || '未設定'}</p>
                      </div>
                      {isNextShort && (
                        <div>
                          <p className="text-sm font-bold">{nextField.label}</p>
                          <p className="mt-2 text-sm">{nextField.value || '未設定'}</p>
                        </div>
                      )}
                    </div>
                  );
                }
              });
            })()}
          </div>
        </div>
        <div className="flex h-[74px] items-center justify-end gap-4 border-t border-solid border-border px-6">
          <div className="w-[94px]">
            <Button type="button" onClick={close} color="secondary">
              キャンセル
            </Button>
          </div>
          <Button type="button" onClick={handleImport}>
            取り込む
          </Button>
        </div>
      </div>
    </Modal>
  );
}
